import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { debounce } from 'underscore';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { IPresenter, StreamType } from 'rev-shared/webrtc/Contract';
import { getLayoutVideo, StreamsInLayout } from 'rev-shared/webrtc/ListenerPresenters';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { AudioCueService, AudioCueUrls } from '../../AudioCue.Service';

import { IProducerLayoutVideo, IProducerOptions } from '../../Contract';
import { ProducerService } from '../../Producer.Service';

import styles from './PresenterVideo.module.less';

@Component({
	selector: 'presenter-video',
	templateUrl: './PresenterVideo.Component.html',
	host: {
		'[class]': 'rootClass'
	},
	standalone: false
})
export class PresenterVideoComponent implements OnChanges, OnInit, OnDestroy {
	@Input() public webcast: WebcastModel;
	@Input() public isProducer: boolean;
	@Input() public name: string;
	@Input() public presenter: IPresenter;
	@Input() public producerOptions: IProducerOptions;
	@Input() public canEditLayout: boolean;
	@Input() public backstageVolume: number;
	@Input() public onstageVolume: number;

	@ViewChild('camVideo')
	public camVideoRef: ElementRef;
	@ViewChild('shareVideo')
	public shareVideoRef: ElementRef;

	public readonly styles = styles;
	public readonly StreamType = StreamType;

	public activeStream: MediaStream;
	public cameraStream: MediaStream;
	public hasCameraVideo: boolean;
	public hasAudio: boolean;
	public captureStream: MediaStream;
	public enableToggle: boolean;
	public iconClass: string;
	public muteAll: boolean;
	public cameraVolume: number;
	public captureVolume: number;
	public rootClass = styles.root;

	public backstageStreamTypes: StreamsInLayout = {} as any;
	public onStageStreamTypes: StreamsInLayout = {} as any;
	public isMicOnStage: boolean;
	public showMenu: boolean;
	public speakerActive: boolean;
	public showMuteBtn: boolean;
	public isMute: boolean;
	private sub: Subscription;
	private isPushToTalkIn: boolean;
	private canHearPushToTalk: boolean = false;
	public canPushToTalk: boolean;
	public currentUserHasMic: boolean;
	public hasOutboundPushToTalk: boolean;
	private endPushToTalk: () => void;

	constructor(
		private readonly AudioCueService: AudioCueService,
		private readonly ProducerService: ProducerService,
		private readonly ListenerConnection: WebRtcListenerConnectionService,
	){}

	public ngOnInit(): void {
		const clearSpeakerActive = debounce(() => this.speakerActive = false, 1000);
		this.sub = this.ListenerConnection.presenters.presenters$.subscribe(() => {
			if(this.ListenerConnection.presenters.isSpeaking(this.presenter.id)) {
				this.speakerActive = true;
				clearSpeakerActive();
			}
		});
		this.sub.add(this.ProducerService.producerStreams$.subscribe(streams => {
			this.currentUserHasMic = !!streams.micStream;
			this.hasOutboundPushToTalk = !!streams.pushToTalk;
			this.updateMenu();
		}));
	}

	public ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.endPushToTalk?.();
	}

	public ngOnChanges(): void {
		const presenter = this.presenter;
		this.cameraStream = presenter.cameraStream;
		this.hasCameraVideo = this.cameraStream?.getVideoTracks().length > 0;
		this.captureStream = presenter.captureStream;
		this.hasAudio = presenter.streams.some(s => s.producer.kind === StreamType.Audio);

		if(!this.activeStream ||
			this.activeStream !== this.cameraStream &&
			this.activeStream !== this.captureStream) {
			this.activeStream = this.hasCameraVideo && this.cameraStream
				|| this.captureStream;
		}

		this.enableToggle = !!(this.hasCameraVideo && this.captureStream);
		this.updateIcon();
		this.updateMenu();
	}

	private updateMenu(): void {
		const presenter = this.presenter;
		const userId = this.webcast.currentUser.id;
		this.backstageStreamTypes = this.ListenerConnection.presenters.checkPresenterLayout(presenter.id, this.producerOptions.backstageLayout);
		this.onStageStreamTypes = this.ListenerConnection.presenters.checkPresenterLayout(presenter.id, this.producerOptions.onStageLayout);
		this.isMicOnStage = this.onStageStreamTypes.audio || this.onStageStreamTypes.speaker || this.onStageStreamTypes.content;
		const hasLayoutOptions = this.canEditLayout && (
			this.showToggleSpeaker() ||
			this.showToggleContent() ||
			this.showToggleAudio());

		const currentUserOnStage = this.isUserOnStage();
		const isPushToTalk = presenter.isPushToTalk;
		const canHearPushToTalk = !!(isPushToTalk && (
				presenter.pushToTalkUser
					? presenter.pushToTalkUser === userId
					: currentUserOnStage));

		if(this.canHearPushToTalk !== canHearPushToTalk) {
			console.log('canHearPushTalk: ', canHearPushToTalk);
			this.playBeep(canHearPushToTalk ? AudioCueUrls.startBeep : AudioCueUrls.stopBeep);
		}

		this.isPushToTalkIn = isPushToTalk;
		this.canHearPushToTalk = canHearPushToTalk;
		this.canPushToTalk = this.currentUserHasMic
			&& !currentUserOnStage
			&& (this.webcast.currentUser.isAssignedEventAdmin || !this.isMicOnStage);
		this.rootClass = this.canHearPushToTalk ? `${styles.root} ${styles.incomingPushTalk}` : styles.root;

		this.showMenu = this.muteAll || !!this.cameraStream || hasLayoutOptions || this.canPushToTalk;

		this.updateVolume();
	}

	private isUserOnStage(): boolean {
		const streams = this.ListenerConnection.presenters.checkPresenterLayout(this.webcast.currentUser.id, this.producerOptions.onStageLayout);
		return streams.audio || streams.speaker || streams.content;
	}

	public showToggleSpeaker(): boolean {
		return this.hasCameraVideo || this.backstageStreamTypes.speaker;
	}

	public showToggleContent(): boolean {
		return !!this.captureStream || this.backstageStreamTypes.content;
	}

	public showToggleAudio(): boolean {
		const s = this.backstageStreamTypes;
		return s.audio ||
			this.cameraStream && !s.content && !s.speaker;
	}

	public toggleStream(): void {
		this.activeStream = this.activeStream === this.cameraStream
			? this.captureStream
			: this.cameraStream;
		this.updateIcon();
	}

	public updateIcon(): void {
		this.iconClass = !this.activeStream ? ''
			: (this.activeStream === this.cameraStream)
				? 'glyphicons display'
				: 'vb-icon vb-icon-camera';
	}

	public toggleMute(): void {
		this.setMute(!this.muteAll);
	}

	public setMute(mute: boolean): void {
		this.muteAll = mute;
		this.updateVolume();
	}

	private updateVolume(): void {
		this.cameraVolume = !this.muteAll && (this.isMicOnStage ? this.onstageVolume : this.backstageVolume) / 100;
		this.captureVolume = !this.muteAll && (this.onStageStreamTypes.content ? this.onstageVolume : this.backstageVolume) / 100;

		if(this.isPushToTalkIn) {
			this.cameraVolume = this.canHearPushToTalk ? 1 : 0;
			this.isMute = !this.cameraVolume;
			return;
		}

		if(this.hasOutboundPushToTalk) {
			this.cameraVolume *= 0.2;
			this.captureVolume *= 0.2;
		}

		this.isMute = this.muteAll || this.presenter.muted || !this.cameraVolume;
	}

	public readonly getDragData: () => IProducerLayoutVideo = () => {
		if(!this.activeStream) {
			return;
		}
		const isCapture = this.activeStream === this.captureStream;
		const type = isCapture ? StreamType.Content : StreamType.Speaker;
		return getLayoutVideo(this.presenter, type);
	};

	public toggleLayoutAudio(): void {
		this.toggleLayoutStream(StreamType.Audio, !this.backstageStreamTypes.audio);
	}

	public addPresenter(type: StreamType, isBackground?: boolean): void {
		this.toggleLayoutStream(type, true, isBackground);
	}

	public removePresenter(type: StreamType): void {
		this.toggleLayoutStream(type, false);
	}

	private toggleLayoutStream(type: StreamType, active: boolean, isBackground?: boolean): void {
		this.ProducerService.togglePresenterPreview(this.presenter, type, isBackground, active)
			.catch(e => console.log('PresenterVideoComponent, toggleLayoutStream: ', e));
	}

	public startPushToTalk(): void {
		if(this.endPushToTalk) {
			throw new Error('already started push to talk');
		}
		this.ProducerService.startPushToTalk(this.presenter.id)
			.then(stop => {
				this.endPushToTalk = stop;
			});
	}

	public stopPushToTalk(): void {
		this.endPushToTalk?.();
		this.endPushToTalk = null;
	}

	public playBeep(url: string): void {
		this.ProducerService.muteScreenshare(true);
		this.AudioCueService.play(url);

		setTimeout(() => this.ProducerService.muteScreenshare(false), 500);
	}

	public checkVideos(): void {
		setTimeout(() => {
			if(this.camVideoRef) {
				this.checkVideo(this.camVideoRef.nativeElement, 'Camera');
			}
			if(this.shareVideoRef) {
				this.checkVideo(this.shareVideoRef.nativeElement, 'ScreenShare');
			}
		}, 1000);
	}


	public checkVideo(video: HTMLVideoElement, type: string): void {
		if(video.paused){
			console.log('PresenterVideo: video did not autoplay. Unpausing video for ', this.name, type);
			video.play();
		}

		else if(!this.isHealthy(video)) {
			console.warn('PresenterVideo: Video not healthy for presenter ', this.name, type);
		}
	}


	public isHealthy(video: HTMLVideoElement): boolean {
		return !video.paused &&
			!video.ended &&
			video.currentTime > 0 &&
			video.readyState >= 2;
	}
}
